import React, { useState } from 'react';

/**
 * Simple diagram renderer - generates diagrams programmatically
 * Creates basic shapes (circles, rectangles, arrows) with interactive labels
 * No templates needed - everything is generated on-the-fly
 */
export function DiagramRenderer({ data }) {
  const { shape = 'rectangle', parts = [], title, width = 400, height = 300 } = data;
  const [hoveredPart, setHoveredPart] = useState(null);

  // Generate base shape based on type
  const renderBaseShape = () => {
    switch (shape) {
      case 'circle':
        return (
          <circle
            cx={width / 2}
            cy={height / 2}
            r={Math.min(width, height) / 3}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
        );

      case 'rectangle':
        return (
          <rect
            x={width * 0.2}
            y={height * 0.2}
            width={width * 0.6}
            height={height * 0.6}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            rx="8"
          />
        );

      case 'triangle':
        return (
          <polygon
            points={`${width/2},${height*0.1} ${width*0.9},${height*0.8} ${width*0.1},${height*0.8}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
        );

      case 'custom':
        // For custom shapes, parts define the entire structure
        return null;

      default:
        return null;
    }
  };

  return (
    <div className="diagram-container w-full max-w-3xl mx-auto">
      {title && (
        <h4 className="text-lg font-semibold mb-3 text-center text-gray-800 dark:text-gray-200">
          {title}
        </h4>
      )}

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-900"
        style={{ minHeight: '200px' }}
      >
        {/* Render base shape */}
        {renderBaseShape()}

        {/* Render parts/components */}
        {parts.map((part, index) => {
          const isHovered = hoveredPart === index;

          return (
            <g key={index}>
              {/* Part shape */}
              {part.shape === 'circle' && (
                <circle
                  cx={part.x}
                  cy={part.y}
                  r={part.size || 20}
                  fill={part.color || '#3b82f6'}
                  stroke="white"
                  strokeWidth="2"
                  opacity={isHovered ? 1 : 0.8}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredPart(index)}
                  onMouseLeave={() => setHoveredPart(null)}
                />
              )}

              {part.shape === 'rectangle' && (
                <rect
                  x={part.x - (part.width || 30) / 2}
                  y={part.y - (part.height || 20) / 2}
                  width={part.width || 30}
                  height={part.height || 20}
                  fill={part.color || '#3b82f6'}
                  stroke="white"
                  strokeWidth="2"
                  rx="4"
                  opacity={isHovered ? 1 : 0.8}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredPart(index)}
                  onMouseLeave={() => setHoveredPart(null)}
                />
              )}

              {/* Label marker */}
              <circle
                cx={part.x}
                cy={part.y}
                r="6"
                fill={isHovered ? '#ef4444' : '#3b82f6'}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredPart(index)}
                onMouseLeave={() => setHoveredPart(null)}
              />

              {/* Label text (always visible) */}
              <text
                x={part.labelX || part.x}
                y={part.labelY || part.y - 15}
                textAnchor="middle"
                fontSize="12"
                fill="currentColor"
                fontWeight="bold"
                style={{ pointerEvents: 'none' }}
              >
                {part.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Hover info panel */}
      {hoveredPart !== null && parts[hoveredPart]?.description && (
        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
          <div className="font-semibold text-blue-900 dark:text-blue-100">
            {parts[hoveredPart].label}
          </div>
          <div className="text-sm text-blue-800 dark:text-blue-200 mt-1">
            {parts[hoveredPart].description}
          </div>
        </div>
      )}

      {/* Parts legend */}
      {parts.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded">
          <h5 className="font-semibold text-sm text-gray-700 dark:text-gray-300 mb-2">
            Components:
          </h5>
          <ul className="grid grid-cols-2 gap-2 text-sm">
            {parts.map((part, index) => (
              <li
                key={index}
                className="flex items-start gap-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                onMouseEnter={() => setHoveredPart(index)}
                onMouseLeave={() => setHoveredPart(null)}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                  style={{ backgroundColor: part.color || '#3b82f6' }}
                />
                <span className="text-gray-700 dark:text-gray-300">{part.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
        Hover over components to see details
      </div>
    </div>
  );
}
